/**
 * Schémas Zod — Projects.
 */

import { z } from "zod";

import { GUARANTEES, LIMITS, PROJECT_STATUSES } from "../constants.js";

import { DateOnlySchema } from "./common.js";
import { ProjectOperationalStateSchema } from "./db-rows.js";
// 9.V.2c-2 (F) — validation SYNTAXIQUE du code assureur (slug), découplée du
// catalogue statique `INSURER_CODES`. La validation MÉTIER (existence/statut
// dans le registre du tenant) se fait au point de persistance, jamais en Zod.
// `project.insurer_codes` = hint de planification non-contraignant → slug seul.
import { InsurerCodeSchema } from "./insurers.js";

const guaranteeCodes = GUARANTEES.map((g) => g.code) as [string, ...string[]];

export const CreateProjectSchema = z.object({
  company_id: z.string().uuid(),
  reference: z.string().max(50).optional(),
  title: z.string().min(3).max(LIMITS.PROJECT_TITLE_MAX),

  broker_id: z.string().uuid().optional(),
  contact_id: z.string().uuid().optional(),

  effect_date: DateOnlySchema.optional(),
  renewal_target_date: DateOnlySchema.optional(),
  deadline_date: DateOnlySchema.optional(),

  // 9.BA.1 — plus obligatoire à la création.
  //
  // Le commentaire ci-dessus le disait déjà : c'est un **hint de planification
  // non contraignant**. Il était pourtant exigé à la première étape du tunnel,
  // avant qu'un conseiller ait pu préparer quoi que ce soit — et il n'a rien à
  // voir avec `quote_request_recipients`, qui désigne les vrais destinataires.
  // On demandait donc les assureurs deux fois, avec deux sens, et la première
  // ne servait pas à l'envoi.
  //
  // Le champ reste, les colonnes restent, l'extraction continue de s'en servir
  // quand il est renseigné.
  insurer_codes: z.array(InsurerCodeSchema).max(10).default([]),
  guarantee_codes: z.array(z.enum(guaranteeCodes)).max(30).default([]),

  notes: z.string().max(LIMITS.NOTE_MAX).optional(),
  requested_commission_rate: z.number().min(0).max(100).multipleOf(0.01).nullable().optional(),
  tags: z.array(z.string().max(50)).max(20).default([]),
});
export type CreateProjectInput = z.infer<typeof CreateProjectSchema>;

export const UpdateProjectSchema = CreateProjectSchema.partial()
  .omit({ company_id: true })
  .extend({
    // 8.D — D1/D2 : `null` accepté pour détacher broker_id / contact_id.
    // L'API doit traiter `null` comme un effacement explicite (SET col = NULL).
    // `undefined` reste "ne touche pas au champ" (champ omis du PATCH).
    broker_id: z.string().uuid().nullable().optional(),
    contact_id: z.string().uuid().nullable().optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    stage_label: z.string().max(100).optional(),
    progress: z.number().int().min(0).max(100).optional(),
  });
export type UpdateProjectInput = z.infer<typeof UpdateProjectSchema>;

export const ProjectFilterSchema = z.object({
  /**
   * 9.BG.1 — filtrer sur l'état DÉRIVÉ, pas sur `status`.
   *
   * C'est ce qui permet de demander « quels dossiers attendent les assureurs ? »
   * en un appel — depuis l'interface comme depuis un agent. `status` reste
   * disponible en filtre secondaire, pour l'archivage notamment.
   */
  operational_state: ProjectOperationalStateSchema.optional(),
  status: z.enum(PROJECT_STATUSES).optional(),
  company_id: z.string().uuid().optional(),
  broker_id: z.string().uuid().optional(),
  // 8.D — D3/D4 : filtres array contains.
  // GET /v1/projects?guarantee_code=amc_total → projets dont
  // `guarantee_codes @> ARRAY['amc_total']`. Idem pour `insurer_code` sur
  // `insurer_codes`. Un seul code à la fois (pas de OR multi-code MVP).
  guarantee_code: z.enum(guaranteeCodes).optional(),
  insurer_code: InsurerCodeSchema.optional(),
  q: z.string().max(100).optional(),
});

/** Explicit human lifecycle command; version prevents stale actions after reopening. */
export const ProjectLifecycleCommandSchema = z
  .object({
    action: z.enum(["lost", "stop", "reopen"]),
    reason: z.string().trim().min(1).max(500),
    expected_version: z.number().int().nonnegative().max(2147483646),
  })
  .strict();
export type ProjectLifecycleCommand = z.infer<typeof ProjectLifecycleCommandSchema>;

export const ProjectLifecycleViewSchema = z.object({
  project_id: z.string().uuid(),
  state: z.enum(["active", "stopped", "lost"]),
  version: z.number().int().nonnegative(),
  reason: z.string().nullable(),
  archived: z.boolean(),
  active_requests: z.number().int().nonnegative(),
  client_followups: z.number().int().nonnegative(),
  queued_messages: z.number().int().nonnegative(),
  in_flight: z.boolean(),
  can_stop: z.boolean(),
  can_mark_lost: z.boolean(),
  can_reopen: z.boolean(),
  can_archive: z.boolean(),
  can_unarchive: z.boolean(),
  history: z.array(
    z.object({
      version: z.number().int(),
      action: z.enum(["lost", "stop", "reopen"]),
      reason: z.string(),
      created_at: z.string(),
    }),
  ),
});
export type ProjectLifecycleView = z.infer<typeof ProjectLifecycleViewSchema>;
